import React, { Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { useAuth } from '@/contexts/AuthContext'
import { AppLayout } from '@/components/layout/AppLayout'
import { ImpersonationProvider } from '@/contexts/ImpersonationContext'
import { ImpersonationBanner } from '@/components/ImpersonationBanner'

// ─── Lazy-loaded page components ─────────────────────────────────────────────

const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = React.lazy(() => import('@/pages/auth/RegisterPage'))
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPasswordPage'))

const OrgListPage = React.lazy(() => import('@/pages/orgs/OrgListPage'))
const CreateOrgPage = React.lazy(() => import('@/pages/orgs/CreateOrgPage'))
const OrgDashboardPage = React.lazy(() => import('@/pages/orgs/OrgDashboardPage'))
const OrgSettingsPage = React.lazy(() => import('@/pages/orgs/OrgSettingsPage'))

const CreateProductPage = React.lazy(() => import('@/pages/products/CreateProductPage'))
const ProductDashboardPage = React.lazy(() => import('@/pages/products/ProductDashboardPage'))
const ProductSettingsPage = React.lazy(() => import('@/pages/products/ProductSettingsPage'))
const UserRequirementsPage = React.lazy(() => import('@/pages/products/UserRequirementsPage'))
const ProductRequirementsPage = React.lazy(() => import('@/pages/products/ProductRequirementsPage'))
const SubRequirementsPage = React.lazy(() => import('@/pages/products/SubRequirementsPage'))
const RecordsPage = React.lazy(() => import('@/pages/products/RecordsPage'))
const HazardsPage = React.lazy(() => import('@/pages/products/HazardsPage'))
const TraceMatrixPage = React.lazy(() => import('@/pages/products/TraceMatrixPage'))
const SoftwareTraceMatrixPage = React.lazy(() => import('@/pages/products/SoftwareTraceMatrixPage'))
const ExportsPage = React.lazy(() => import('@/pages/products/ExportsPage'))
const ImportWizardPage = React.lazy(() => import('@/pages/products/ImportWizardPage'))
const TestProtocolsPage = React.lazy(() => import('@/pages/products/TestProtocolsPage'))
const TestProtocolDetailPage = React.lazy(() => import('@/pages/products/TestProtocolDetailPage'))
const CoveragePage = React.lazy(() => import('@/pages/products/CoveragePage'))
const ControlsCoveragePage = React.lazy(() => import('@/pages/products/ControlsCoveragePage'))
const BillingPage = React.lazy(() => import('@/pages/orgs/BillingPage'))
const ControlsPage = React.lazy(() => import('@/pages/orgs/ControlsPage'))
const AuditLogPage = React.lazy(() => import('@/pages/orgs/AuditLogPage'))

const AdminLayout = React.lazy(() => import('@/components/layout/AdminLayout').then(m => ({ default: m.AdminLayout })))
const AdminUsersPage = React.lazy(() => import('@/pages/admin/AdminUsersPage'))
const AdminUserDetailPage = React.lazy(() => import('@/pages/admin/AdminUserDetailPage'))
const AdminOrgsPage = React.lazy(() => import('@/pages/admin/AdminOrgsPage'))
const AdminOrgDetailPage = React.lazy(() => import('@/pages/admin/AdminOrgDetailPage'))

// ─── Route guards ─────────────────────────────────────────────────────────────

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (user) return <Navigate to="/orgs" replace />
  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}


// ─── Loading fallback ─────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

// ─── App ──────────────────────────────────────────────────────────────────────

function App() {
  return (
    <ImpersonationProvider>
      <ImpersonationBanner />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Root redirect */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/orgs" replace />
              </ProtectedRoute>
            }
          />

          {/* Org list */}
          <Route
            path="/orgs"
            element={
              <ProtectedRoute>
                <OrgListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/new"
            element={
              <ProtectedRoute>
                <CreateOrgPage />
              </ProtectedRoute>
            }
          />

          {/* Unified layout — handles both org-level and product-level pages */}
          <Route
            path="/orgs/:orgSlug"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Org-level pages */}
            <Route index element={<OrgDashboardPage />} />
            <Route path="settings" element={<OrgSettingsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="controls" element={<ControlsPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="products/new" element={<CreateProductPage />} />

            {/* Product-level pages — AppLayout reads :productId from URL */}
            <Route path="products/:productId">
              <Route index element={<ProductDashboardPage />} />
              <Route path="settings" element={<ProductSettingsPage />} />
              <Route path="user-requirements" element={<UserRequirementsPage />} />
              <Route path="product-requirements" element={<ProductRequirementsPage />} />
              <Route path="sub-requirements" element={<SubRequirementsPage />} />
              <Route path="records" element={<RecordsPage />} />
              <Route path="hazards" element={<HazardsPage />} />
              <Route path="trace-matrix" element={<TraceMatrixPage />} />
              <Route path="software-trace-matrix" element={<SoftwareTraceMatrixPage />} />
              <Route path="exports" element={<ExportsPage />} />
              <Route path="import" element={<ImportWizardPage />} />
              <Route path="test-protocols" element={<TestProtocolsPage />} />
              <Route path="test-protocols/:protocolId" element={<TestProtocolDetailPage />} />
              <Route path="coverage" element={<CoveragePage />} />
              <Route path="controls-coverage" element={<ControlsCoveragePage />} />
            </Route>
          </Route>

          {/* Admin panel — AdminLayout does its own auth/superadmin guard internally */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="orgs" element={<AdminOrgsPage />} />
            <Route path="orgs/:orgId" element={<AdminOrgDetailPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/orgs" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </ImpersonationProvider>
  )
}

export default App
